import type { CalendarEvent } from './agenda';

// A day as minutes from local midnight: where events sit, where there is room for more.

export const DAY_START = 8 * 60;
export const DAY_END = 20 * 60;

export type Span = { start: number; end: number };
export type Timed = Span & { event: CalendarEvent };

const minutesOf = (instant: string, day: string) => {
  const date = new Date(instant);
  const midnight = new Date(`${day}T00:00:00`);
  return Math.round((date.getTime() - midnight.getTime()) / 60_000);
};

/** The timed events of a day in minutes, cut to the day; all-day events are left out. */
export function timedOn(events: readonly CalendarEvent[], day: string): Timed[] {
  return events
    .filter((event) => !event.allDay)
    .map((event) => ({
      event,
      start: Math.max(0, minutesOf(event.startsAt, day)),
      end: Math.min(24 * 60, minutesOf(event.endsAt, day)),
    }))
    .filter((item) => item.end > item.start)
    .sort((a, b) => a.start - b.start || b.end - a.end);
}

/**
 * Columns for events that overlap, side by side like in any calendar: each item gets its column
 * and how many columns its group of overlapping events uses.
 */
export function layoutDay<T extends Span>(items: readonly T[]) {
  const placed: { item: T; column: number; columns: number }[] = [];
  let group: { item: T; column: number; columns: number }[] = [];
  let groupEnd = -1;
  const close = () => {
    const columns = Math.max(0, ...group.map((entry) => entry.column)) + 1;
    for (const entry of group) entry.columns = columns;
    placed.push(...group);
    group = [];
  };
  for (const item of [...items].sort((a, b) => a.start - b.start || b.end - a.end)) {
    if (group.length > 0 && item.start >= groupEnd) close();
    const ends: number[] = [];
    for (const entry of group)
      ends[entry.column] = Math.max(ends[entry.column] ?? 0, entry.item.end);
    let column = ends.findIndex((end) => end <= item.start);
    if (column === -1) column = ends.length;
    group.push({ item, column, columns: 1 });
    groupEnd = Math.max(groupEnd, item.end);
  }
  if (group.length > 0) close();
  return placed;
}

/** Busy time as separate spans, overlaps joined. */
export function busySpans(items: readonly Span[]): Span[] {
  const merged: Span[] = [];
  for (const { start, end } of [...items].sort((a, b) => a.start - b.start)) {
    const last = merged[merged.length - 1];
    if (last && start <= last.end) last.end = Math.max(last.end, end);
    else merged.push({ start, end });
  }
  return merged;
}

/**
 * Starts of `count` blocks of `minutes`, from `from` on, in the free time between `dayStart` and
 * `dayEnd`, with `pause` minutes between them. Fewer come back when the day is full.
 */
export function planBlocks(
  busy: readonly Span[],
  count: number,
  minutes: number,
  from: number,
  pause = 0,
  dayStart = DAY_START,
  dayEnd = DAY_END,
): number[] {
  const taken = busySpans(busy);
  const starts: number[] = [];
  let cursor = Math.max(from, dayStart);
  while (starts.length < count && cursor + minutes <= dayEnd) {
    const clash = taken.find((span) => span.start < cursor + minutes && span.end > cursor);
    if (clash) {
      cursor = clash.end;
      continue;
    }
    starts.push(cursor);
    taken.push({ start: cursor, end: cursor + minutes + pause });
    taken.sort((a, b) => a.start - b.start);
    cursor += minutes + pause;
  }
  return starts;
}

/** The day's load between `dayStart` and `dayEnd`: busy, of which planned for tasks, and free. */
export function dayLoad(items: readonly Timed[], dayStart = DAY_START, dayEnd = DAY_END) {
  const clip = (span: Span) => ({
    start: Math.max(span.start, dayStart),
    end: Math.min(span.end, dayEnd),
  });
  const total = (spans: readonly Span[]) =>
    busySpans(spans.map(clip).filter((span) => span.end > span.start)).reduce(
      (sum, span) => sum + span.end - span.start,
      0,
    );
  const busy = total(items);
  return {
    busy,
    planned: total(items.filter((item) => item.event.taskId)),
    free: dayEnd - dayStart - busy,
  };
}

/** "9:05" from minutes after midnight. */
export const clockOf = (minutes: number) =>
  `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;

/** "09:05", for <input type="time">. */
export const timeValue = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** "1 h 30 min", "45 min", "2 h". */
export function duration(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** The next quarter of an hour from now, in minutes after midnight. */
export const nextQuarter = (date = new Date()) =>
  Math.ceil((date.getHours() * 60 + date.getMinutes()) / 15) * 15;
